import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslate } from '@tolgee/react'
import { Alert, Button, PasswordField, TextField } from '@kcm/ui'
import { ErrorCode, TwoFactorRequest } from '@kcm/contracts'
import { Turnstile } from '../components/Turnstile'
import { useSite } from '../components/SiteProvider'
import { useLocale } from '../hooks/useLocale'
import { ApiError } from '../lib/api'
import { endpoints } from '../lib/endpoints'
import { asApiError, fieldError } from '../lib/validation'

/**
 * Signing in (PLAN.md §10.1).
 *
 * The legacy `login.php` asked the admin REST API to judge the password
 * (`check_credentials`) and then minted a session with `direct_login`. Two
 * consequences, both invisible from the outside: the onboarding server, not the
 * CRM, decided whether a password was right, and any second factor the client
 * had enabled was never consulted (finding #15). This screen talks to an
 * endpoint that goes through the CRM's own client login, which is why there is
 * a second phase here at all.
 *
 * Nothing about the account is decided in the browser. The server answers with
 * either a hand-off URL or a challenge, and every way it can refuse arrives as
 * the same message — an unknown address and a wrong password are not
 * distinguishable from here, on purpose (finding #17).
 */
interface Challenge {
  id: string
  attemptsRemaining: number
  /** The CRM's own wording for where it sent the code, when it says. */
  message?: string | undefined
}

export function Login() {
  const [challenge, setChallenge] = useState<Challenge | null>(null)
  // Set when a second factor runs out of time or attempts, so the credentials
  // form can say why it is being shown again rather than appearing to have
  // thrown the sign-in away.
  const [restarted, setRestarted] = useState<string | null>(null)

  return challenge ? (
    <TwoFactorPhase
      challenge={challenge}
      onRestart={(message) => {
        setChallenge(null)
        setRestarted(message)
      }}
    />
  ) : (
    <CredentialsPhase
      notice={restarted}
      onChallenge={(next) => {
        setChallenge(next)
        setRestarted(null)
      }}
    />
  )
}

/**
 * The hand-off is a full navigation, not a router push.
 *
 * The URL is single-use and minted by the CRM for a different origin; the
 * client cabinet is not part of this app and there is nothing to come back to.
 */
function handOff(url: string): void {
  window.location.assign(url)
}

// ── phase 1: the password ────────────────────────────────────────────────────

function CredentialsPhase({
  notice,
  onChallenge,
}: {
  notice: string | null
  onChallenge: (challenge: Challenge) => void
}) {
  const site = useSite()
  const { t } = useTranslate()
  const locale = useLocale()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [token, setToken] = useState('')
  // A Turnstile token is single-use, so a failed attempt has to be given a
  // fresh widget or the retry fails for a reason the user cannot see.
  const [widget, setWidget] = useState(0)
  const [challengeUnavailable, setChallengeUnavailable] = useState(false)

  const signIn = useMutation({
    mutationFn: () =>
      endpoints.login({
        email: email.trim().toLowerCase(),
        password,
        ...(token ? { turnstileToken: token } : {}),
        locale,
      }),
    onSuccess: (res) => {
      if (res.status === 'ok') return handOff(res.url)
      onChallenge({
        id: res.challengeId,
        attemptsRemaining: res.attemptsRemaining,
        message: res.message,
      })
    },
    onError: () => {
      setToken('')
      setWidget((n) => n + 1)
      // The password is deliberately left in place. The likeliest cause is a
      // typo, and clearing it makes them retype a long string to fix one
      // character.
    },
  })

  const error = signIn.error instanceof ApiError ? signIn.error : null

  // The server holds the real gate (§18.2); this only avoids a submit that is
  // certain to be refused. A challenge that never loads must not lock the form.
  const blocked = site.turnstileSiteKey !== null && !token && !challengeUnavailable

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (!signIn.isPending) signIn.mutate()
      }}
    >
      <div>
        <h1 className="text-xl font-bold text-brand">{t('login.title')}</h1>
        <p className="mt-1 text-sm text-muted">{t('login.subtitle')}</p>
      </div>

      {notice && !error && <Alert tone="info">{notice}</Alert>}

      {error && (
        <Alert tone="error" title={titleFor(error)}>
          {error.message}
        </Alert>
      )}

      <TextField
        label="Email address"
        type="email"
        name="email"
        inputMode="email"
        autoComplete="username"
        autoFocus
        required
        value={email}
        error={fieldError(error, 'email')}
        onChange={(e) => setEmail(e.target.value)}
      />

      <PasswordField
        label="Password"
        required
        name="current-password"
        // `current-password`, not `new-password`: this is the one a manager
        // should offer to fill, and never the one it offers to generate.
        autoComplete="current-password"
        // No strength checklist. The field's default rules are for choosing a
        // password; here they would grade one the account already has, and tell
        // anyone whose password predates the policy that it is wrong when the
        // CRM will accept it. `LoginRequest.password` is length-bounded only,
        // for the same reason.
        rules={[]}
        value={password}
        error={fieldError(error, 'password')}
        onChange={(e) => setPassword(e.target.value)}
      />

      {site.turnstileSiteKey && (
        <Turnstile
          siteKey={site.turnstileSiteKey}
          resetKey={widget}
          locale={locale}
          onToken={(next) => {
            setToken(next)
            if (next) setChallengeUnavailable(false)
          }}
          onUnavailable={() => setChallengeUnavailable(true)}
        />
      )}

      {challengeUnavailable && !error && (
        <Alert tone="info" title="The bot check didn't load">
          You can still continue — we'll confirm it when you submit.
        </Alert>
      )}

      <Button
        type="submit"
        disabled={signIn.isPending || email.trim() === '' || password === '' || blocked}
      >
        {signIn.isPending ? 'Signing in…' : t('login.title')}
      </Button>

      <div className="flex flex-col gap-2 text-sm">
        <Link className="text-brand underline" to="/forgot-password">
          {t('login.forgot')}
        </Link>
        <Link className="text-muted underline" to="/">
          {t('login.createAccount')}
        </Link>
      </div>
    </form>
  )
}

// ── phase 2: the second factor ───────────────────────────────────────────────

function TwoFactorPhase({
  challenge,
  onRestart,
}: {
  challenge: Challenge
  onRestart: (notice: string | null) => void
}) {
  const [code, setCode] = useState('')

  const verify = useMutation({
    mutationFn: async () => {
      const parsed = TwoFactorRequest.safeParse({ challengeId: challenge.id, code })
      // A raw ZodError is not an ApiError and would render as nothing at all,
      // leaving a button that appears to do something and does not.
      if (!parsed.success) throw asApiError(parsed.error)
      return endpoints.twoFactor(parsed.data)
    },
    onSuccess: (res) => handOff(res.url),
    onError: (err) => {
      setCode('')
      // The half-finished login behind this challenge is gone — timed out,
      // spent, or out of attempts. Only the password can start another one.
      if (err instanceof ApiError && err.code === ErrorCode.TWO_FACTOR_EXPIRED) {
        onRestart(err.message)
      }
    },
  })

  const error = verify.error instanceof ApiError ? verify.error : null

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (!verify.isPending) verify.mutate()
      }}
    >
      <div>
        <h1 className="text-xl font-bold text-brand">Two-step verification</h1>
        <p className="mt-1 text-sm text-muted">
          {challenge.message ?? 'Enter the code from your authenticator app.'}
        </p>
      </div>

      {error && (
        <Alert tone="error" title={titleFor(error)}>
          {error.message}
          {/* The server counts the attempts, so the warning arrives before the
              lockout rather than with it. */}
          {fieldError(error, 'code') && <span className="block">{fieldError(error, 'code')}</span>}
        </Alert>
      )}

      {/*
        A plain field rather than the six-box `OtpInput`. An authenticator gives
        six digits, but a recovery code is longer and not always numeric, and a
        code the CRM would have accepted must not be unenterable here.
      */}
      <TextField
        label="Verification code"
        name="one-time-code"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        required
        value={code}
        error={fieldError(error, 'code')}
        onChange={(e) => setCode(e.target.value)}
      />

      <Button type="submit" disabled={verify.isPending || code.trim().length < 4}>
        {verify.isPending ? 'Checking…' : 'Verify'}
      </Button>

      <button
        type="button"
        className="text-sm text-muted underline"
        onClick={() => onRestart(null)}
      >
        Sign in with a different account
      </button>
    </form>
  )
}

// ── shared ───────────────────────────────────────────────────────────────────

/**
 * A short heading per error class. The body is always the server's message: it
 * is already display copy, already localised, and — for a refused sign-in —
 * deliberately the same sentence whatever the real reason was.
 */
export function titleFor(error: ApiError): string {
  switch (error.code) {
    case ErrorCode.INVALID_CREDENTIALS:
      return "That didn't work"
    case ErrorCode.TWO_FACTOR_INVALID:
      return 'Incorrect code'
    case ErrorCode.TWO_FACTOR_EXPIRED:
      return 'Please sign in again'
    case ErrorCode.RATE_LIMITED:
      return 'Too many attempts'
    case ErrorCode.TURNSTILE_FAILED:
      return 'Check failed'
    case ErrorCode.OTP_DELIVERY_FAILED:
      return "We couldn't send the code"
    case ErrorCode.OTP_LOCKED:
      return 'Too many wrong codes'
    case ErrorCode.OTP_EXPIRED:
      return 'That code has expired'
    case ErrorCode.CRM_UNAVAILABLE:
      return 'Temporarily unavailable'
    default:
      return 'Something went wrong'
  }
}
